/* eslint-disable no-loop-func */
/**
 * 并发分批拉取所有符合条件的ID列表（用于全选功能加速与进度展示）
 *
 * 需要传入一个 fetchPage 函数，调用方式：fetchPage(pageNum)，页号从1开始，
 * 返回 Promise，resolve 时返回 { total, list }（list 为当前页的ID数组，total为总条数）；
 * reject/抛错时整体视为失败。
 *
 * 实现思路：先请求第1页拿到 total，计算出总页数，再以固定并发数并发请求剩余页，
 * 每当一批结果返回就更新进度 onProgress(percent)。最终 resolve 出包含所有ID的数组。
 *
 * @param {Function} fetchPage  单页请求函数，返回 Promise<{total, list}>
 * @param {number}   pageSize   每页条数（须与后端 pageSize 保持一致，用于计算总页数）
 * @param {Function} onProgress 进度回调，参数为0~100的整数百分比
 * @returns {Promise<Array>} 全量ID数组
 */
export function fetchAllIdsInParallel(fetchPage, pageSize, onProgress) {
    return new Promise((resolve, reject) => {
        fetchPage(1).then(r1 => {
            if (!r1 || !Array.isArray(r1.list)) {
                reject(new Error('no data'));
                return;
            }
            const total = r1.total || 0;
            let allIds = r1.list.slice();
            const totalPages = Math.max(1, Math.ceil(total / pageSize));
            let done = 1;
            let failed = false;
            const report = () => {
                const pct = total > 0 ? Math.min(Math.round((allIds.length / total) * 100), 100) : 100;
                if (onProgress) onProgress(pct);
            };
            report();
            if (totalPages <= 1) {
                resolve(allIds);
                return;
            }
            const CONCURRENCY = 8;
            const queue = [];
            for (let p = 2; p <= totalPages; p++) queue.push(p);
            let active = 0;
            const startNext = () => {
                while (active < CONCURRENCY && queue.length && !failed) {
                    const p = queue.shift();
                    active++;
                    fetchPage(p).then(r => {
                        active--;
                        if (failed) return;
                        if (!r || !Array.isArray(r.list)) {
                            failed = true;
                            reject(new Error('fetch page ' + p + ' failed'));
                            return;
                        }
                        allIds = allIds.concat(r.list);
                        done++;
                        report();
                        if (done >= totalPages) {
                            resolve(allIds);
                            return;
                        }
                        startNext();
                    }).catch(e => {
                        active--;
                        if (!failed) {
                            failed = true;
                            reject(e);
                        }
                    });
                }
            };
            startNext();
        }).catch(reject);
    });
}